import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SeoProps {
  title: string;
  description?: string;
  path?: string; // path segment like '/pricing'
  ogImage?: string; // absolute or relative (will resolve against baseUrl)
  siteName?: string;
  ogType?: string; // website, article, etc.
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: object | object[]; // optional structured data to inject
  includeWebPageLd?: boolean; // include a basic WebPage schema from props
}

export const Seo: React.FC<React.PropsWithChildren<SeoProps>> = ({
  title,
  description,
  path,
  ogImage,
  siteName,
  ogType,
  twitterCard,
  jsonLd,
  includeWebPageLd,
  children,
}) => {
  const baseUrl =
    (import.meta as any)?.env?.VITE_DEMO_CANONICAL_BASE || 'https://montana.toolboxlogic.com';
  const url = path ? `${baseUrl}${path}` : baseUrl;
  const desc = description ?? 'ToolboxLogic Montana Community Management Platform';
  const defaultOgImage = `${baseUrl}/Community%20App%20-%20Logo.png`;
  const resolvedOgImage = (() => {
    if (!ogImage) return defaultOgImage;
    if (ogImage.startsWith('http://') || ogImage.startsWith('https://')) return ogImage;
    // resolve relative path against baseUrl
    const trimmed = ogImage.startsWith('/') ? ogImage : `/${ogImage}`;
    return `${baseUrl}${trimmed}`;
  })();
  const site = siteName ?? 'ToolboxLogic Montana';
  const ogTypeValue = ogType ?? 'website';
  const twitterCardValue = twitterCard ?? 'summary_large_image';

  const jsonLdPayloads: object[] = [];
  if (includeWebPageLd) {
    jsonLdPayloads.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: desc,
      url,
    });
  }
  if (jsonLd) {
    if (Array.isArray(jsonLd)) jsonLdPayloads.push(...jsonLd);
    else jsonLdPayloads.push(jsonLd);
  }
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content={ogTypeValue} />
        <meta property="og:site_name" content={site} />
        <meta property="og:image" content={resolvedOgImage} />
        {/* Twitter */}
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={desc} />
        <meta name="twitter:card" content={twitterCardValue} />
        <meta name="twitter:image" content={resolvedOgImage} />
        <link rel="canonical" href={url} />
        {jsonLdPayloads.map((payload, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(payload)}
          </script>
        ))}
      </Helmet>
      {children}
    </>
  );
};
